/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Refine and collaborate", description = "Refine generated ads in the chat or the visual editor, and collaborate with comments before you launch.", lastUpdatedDate = new Date(1787097600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Refine and collaborate",
        description: "Refine generated ads in the chat or the visual editor, and collaborate with comments before you launch.",
        lastUpdatedDate: "Wed Aug 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Prompt the agent",
          "depth": 0,
          "id": "prompt-the-agent"
        }, {
          "value": "Edit with the visual editor",
          "depth": 0,
          "id": "edit-with-the-visual-editor"
        }, {
          "value": "Comment on an ad",
          "depth": 0,
          "id": "comment-on-an-ad"
        }, {
          "value": "Track comment activity",
          "depth": 0,
          "id": "track-comment-activity"
        }, {
          "value": "Favorite, queue, and export ads",
          "depth": 0,
          "id": "favorite-queue-and-export-ads"
        }, {
          "value": "What's next",
          "depth": 0,
          "id": "whats-next"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/ad-studio/ads",
                    children: "Generated ads to refine"
                  })
                })
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Refine the concepts Ad Studio generates until they meet your standards: in the chat, in the visual editor, or with comments from your team."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.p, {
        children: "Ad Studio never launches an ad without your approval. Review what the agent generates, refine it, and collaborate with your team before anything goes out."
      }), "\n", _jsx(_components.h2, {
        id: "prompt-the-agent",
        children: "Prompt the agent"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-edit.png",
          alt: "Ad editing workspace with agent chat, brief, concept, and size variants"
        })
      }), "\n", _jsx(_components.p, {
        children: "After Ad Studio generates the first batch, the editing workspace opens. Review the generated ads, then use the chat to describe what you want to change."
      }), "\n", _jsx(_components.p, {
        children: "Describe the change you want in the chat. For example:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "\"Make the headline more direct and lighten the palette.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Swap the product image for the blue variant.\""
        }), "\n", _jsx(_components.li, {
          children: "\"Resize this for Instagram Stories.\""
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The agent explains what it changed, so you can judge each round in context. Specify what should stay the same in each prompt. Otherwise, regenerated ads can drift from the version you approved. See ", _jsx(_components.a, {
          href: "/ad-studio/prompting-guide#iterate-on-results-in-the-chat",
          children: "Iterate on results in the chat"
        }), " for the patterns that work."]
      }), "\n", _jsx(_components.h2, {
        id: "edit-with-the-visual-editor",
        children: "Edit with the visual editor"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-visual-editor.png",
          alt: "Visual editor with layers panel, timeline, and property controls"
        })
      }), "\n", _jsx(_components.p, {
        children: "Switch to the visual editor when you want to adjust the creative directly instead of prompting the agent. Every ad is a layered composition of headline, CTA, imagery, and background, so you can change individual elements without regenerating the ad."
      }), "\n", _jsx(_components.p, {
        children: "Use the editor to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Adjust the layout"
          }), " — Move or resize elements, change their position or opacity, and reorder layers."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Revise copy"
          }), " — Edit text directly without regenerating the rest of the creative."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Add motion"
          }), " — Animate individual layers and use the timeline to preview how the ad changes over time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Edit video clips"
          }), " — Split and trim clips, then move, resize, or crop video layers."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To split a video clip, right-click the video layer and select ", _jsx(_components.strong, {
          children: "Split"
        }), " (or press ", _jsx(_components.code, {
          children: "⌘B"
        }), ") to cut at the playhead. Drag either end of the clip to trim it."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-timeline-split.png",
          alt: "Timeline context menu with Split selected, cutting a video clip at the playhead"
        })
      }), "\n", _jsx(_components.p, {
        children: "Edits in the editor and the chat stay in sync, so you can move between them at any time."
      }), "\n", _jsx(_components.h2, {
        id: "comment-on-an-ad",
        children: "Comment on an ad"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-comments.png",
          alt: "Comment mode on an ad with pins on the creative and the comments rail open"
        })
      }), "\n", _jsx(_components.p, {
        children: "Use comments to review an ad with your teammates. Pin feedback to a specific part of the creative so everyone can see what the note refers to, then reply in the thread and mark it resolved when the feedback is addressed."
      }), "\n", _jsx(_components.p, {
        children: "Select the comments icon above the ad to turn on comment mode. Direct editing pauses while you comment, but you can still pan and zoom around the creative."
      }), "\n", _jsx(_components.p, {
        children: "To leave a comment:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Select a spot on the ad to drop a pin."
        }), "\n", _jsxs(_components.li, {
          children: ["Write your feedback. Type ", _jsx(_components.code, {
            children: "@"
          }), " to mention a teammate — they get an email with a link to the comment."]
        }), "\n", _jsxs(_components.li, {
          children: ["Press ", _jsx(_components.strong, {
            children: "Enter"
          }), " or select the send button to post."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Your comment shows as a pin on the creative and in the comments rail on the right. Select any pin to open its thread, reply, or mark it resolved once the feedback is handled. To reposition a pin, drag it or nudge it with the arrow keys."
      }), "\n", _jsx(_components.p, {
        children: "Comments attach to the size you're viewing, so a note on the 1:1 version stays with that size rather than the whole set. Switch sizes to see or add comments on another format."
      }), "\n", _jsx(_components.p, {
        children: "You can comment on both static and video ads. On a video ad, a comment is anchored to a moment in the clip rather than a fixed spot, and each one shows the timestamp it points to, so feedback lands on the right frame."
      }), "\n", _jsx(_components.p, {
        children: "When a thread needs attention, filter the rail to your own threads, unread comments, or resolved ones. Opening a thread marks it read, and you can mark it unread again from the thread menu to revisit it later."
      }), "\n", _jsx(_components.h2, {
        id: "track-comment-activity",
        children: "Track comment activity"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-comment-notifications.png",
          alt: "The Notifications panel open from the bell, with Unread and All tabs"
        })
      }), "\n", _jsx(_components.p, {
        children: "The bell above the Ads page collects comment activity across the workspace, with a dot when something is unread. Select it to open the feed:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Unread"
          }), " is your inbox — comments you haven't caught up on, minus your own."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), " is every comment in the workspace, newest first."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Select any notification to open the ad and jump to its thread. Select the check to mark everything read at once."
      }), "\n", _jsx(_components.h2, {
        id: "favorite-queue-and-export-ads",
        children: "Favorite, queue, and export ads"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/ads/as-ads-toolbar.png",
          alt: "Ad toolbar with the overflow menu open"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The toolbar above an open ad is where you act on it once it looks right. Favorite an ad to collect it under ", _jsx(_components.strong, {
          children: "My favorites"
        }), ", ", _jsx(_components.a, {
          href: "/ad-studio/launch#queue-ads-for-launch",
          children: "queue it for launch"
        }), ", or download every generated size at once. An overflow menu holds the rest: edit the ad, file it in a folder, open its chat or edit history, and delete it. To hand off to your design team, ", _jsx(_components.a, {
          href: "/ad-studio/figma",
          children: "open the ad in Figma"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "whats-next",
        children: "What's next"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/launch",
            children: "Launch ads: Queue finished ads and send them to your ad platforms"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/figma",
            children: "Hand off to Figma: Bring a finished ad into Figma for final layout"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/prompting-guide",
            children: "Prompting guide: What to put in a prompt, and templates for every format"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
